import { Fragment, useEffect, useState } from "react";
import { useWindowSiteStore } from "@store/windowSiteStore";
import {
    AspectRatio,
    Avatar,
    Box,
    Flex,
    Grid,
    GridItem,
    Icon,
    Image,
    Link,
    VStack,
} from "@chakra-ui/react";
import { GetAccess } from "@components/site/GetAccess";
import GradientButton from "@components/ui/GradientButton";
import { scale } from "@utils/helpers";
import { BsLightningChargeFill } from "react-icons/bs";
import { FiCheck } from "react-icons/fi";
import { SignInModalState, useBasicStore } from "@store/basicStore";
import useUser from "@hooks/useUser";
import { MemoSignedInUserAvatar } from "@components/auth/SignedInUserAvatar";
import { useRouter } from "next/router";
import NextLink from "next/link";

const FREE_FEATURES = [
    "Best rarity visualizations Anywhere. Period.",
    "Robust metadata filtering to pinpoint your perfect match",
    "Lowest listings across Multiple marketplaces",
    "See all rarity rankings vs price all-in-one view",
    "Real-time data refresh",
    <Box key="ff-soon-1">
        Portfolio gallery view{" "}
        <Box as="span" color="lavenderGray">
            (soon)
        </Box>
    </Box>,
];

const PRO_FEATURES = [
    "Price estimates for all Collections on BR",
    "Confidence scores for all Price estimates on BR",
    "Hottest deals presorted At the top of each project",
    <Box key="pf-soon-1">
        Wallet Compare - Size up Your NFTs in context{" "}
        <Box as="span" color="lavenderGray">
            (soon)
        </Box>
    </Box>,
    <Box key="pf-soon-2">
        Hottest deals across ALL NFT collections{" "}
        <Box as="span" color="lavenderGray">
            (soon)
        </Box>
    </Box>,
];

const UBER_FEATURES = [
    "Top Secret features only Available to hodlers",
    "Exclusive access only Available to hodlers",
    "No subscription fees ever",
    "Always the latest, greatest, guaranteed unlimited",
    "Limited # of passes avail. in quirky Dad / Mom form",
];

type PricingTiersSectionProps = {};

export const PricingTiersSection = ({}: PricingTiersSectionProps) => {
    const router = useRouter();
    const { user, status } = useUser();
    const isDesktop = useWindowSiteStore((state) => state.isDesktop);
    const windowWidth = useWindowSiteStore((state) => state.windowWidth);
    const openSignInModal = (state: SignInModalState) => {
        useBasicStore.setState((oldState) => {
            return {
                signInModalState: state,
            };
        });
    };

    const hanleOnSignUp = () => {
        openSignInModal(SignInModalState.SignUp);
    };

    const navigateToProfile = () => {
        if (status === "authenticated") router.push("/user/me", "/user/me");
    };

    const minSize = isDesktop ? 960 : 375;
    const maxSize = isDesktop ? 1200 : 960;
    const horizontalGrid = windowWidth > 600;

    const SignedInLabel = () => {
        if (status === "unauthenticated") return null;

        return (
            <Flex
                w="100%"
                justify="center"
                align="center"
                fontSize={scale(windowWidth, minSize, maxSize, 18, 22)}
                py="4px"
            >
                <Flex gap={3} align="center" justify="center">
                    <NextLink shallow href="/user/me" passHref>
                        <Link href="/user/me">
                            <MemoSignedInUserAvatar
                                name={user?.name || ""}
                                image={user?.image || ""}
                                buttonSize={"30px"}
                                isMobile={false}
                            />
                        </Link>
                    </NextLink>
                    <Box textAlign="center">You’re all set!</Box>
                </Flex>
            </Flex>
        );
    };

    const FREE_TIER = {
        name: "free tier",
        bgColor: "spaceCadetCardAlpha.600",
        isMostPopular: false,
        image: "/images/profile/free-profile-yeti.png",
        title: "Free",
        tagline: "Everyone welcome, free forever",
        pricBgColor: "spaceCadet",
        price: "$0",
        priceDescription: "Get started now",
        priceAction:
            status != "authenticated" ? (
                <GradientButton
                    onClick={hanleOnSignUp}
                    label="Sign Up for Free"
                    buttonStyle={{ fontSize: scale(windowWidth, minSize, maxSize, 18, 22), px: 4 }}
                />
            ) : (
                SignedInLabel()
            ),
        featuresHeading: "Everything for free:",
        features: FREE_FEATURES,
    };

    const PRO_TIER = {
        name: "pro tier",
        bgColor: "#E9376626",
        isMostPopular: true,
        image: "/images/profile/pro-profile-yeti.png",
        title: "Pro",
        tagline: "Collectors & Connoisseurs",
        pricBgColor: "#282D4177",
        price: "$0 (Limited-time)",
        priceDescription: "Get started now",
        priceAction:
            status != "authenticated" ? (
                <GradientButton
                    onClick={hanleOnSignUp}
                    label="Sign Up for Free"
                    buttonStyle={{ fontSize: scale(windowWidth, minSize, maxSize, 18, 22), px: 4 }}
                />
            ) : (
                SignedInLabel()
            ),
        featuresHeading: "Everything in free, plus:",
        features: PRO_FEATURES,
    };

    const UBER_TIER = {
        name: "uber tier",
        bgColor: "spaceCadetCardAlpha.600",
        isMostPopular: false,
        image: "/images/profile/uber-profile-yeti.png",
        title: (
            <Flex
                justify="center"
                align="center"
                fontFamily="regular"
                fontSize={scale(windowWidth, minSize, maxSize, 36, 48)}
                lineHeight="110%"
            >
                <Icon
                    as={BsLightningChargeFill}
                    fontSize={scale(windowWidth, minSize, maxSize, 24, 36)}
                    p={-20}
                    m={0}
                />
                <Box>Uber</Box>
            </Flex>
        ),
        tagline: "Maximum everything",
        pricBgColor: "spaceCadet",
        price: "1-time purchase",
        priceDescription: "Market rates of our NFT apply",
        priceAction: (
            <GetAccess w="100%">
                <Box w="100%">
                    <GradientButton
                        label="Choose your NFT"
                        buttonStyle={{
                            fontSize: scale(windowWidth, minSize, maxSize, 18, 22),
                            px: 4,
                        }}
                    />
                </Box>
            </GetAccess>
        ),
        featuresHeading: "Everything in pro, plus:",
        features: UBER_FEATURES,
    };

    const TierGridItem = ({ tier }) => {
        return (
            <>
                <GridItem
                    pos="relative"
                    borderRadius="15px"
                    border="2px solid"
                    borderColor="blackCoral"
                    bgColor={tier.bgColor}
                    px={2}
                    pt={8}
                    pb={20}
                    textAlign="center"
                >
                    <VStack gap={8}>
                        <AspectRatio minW="90%" ratio={1}>
                            <Box
                                borderRadius="50em"
                                border="1px solid"
                                borderColor="lavenderGray"
                                overflow="hidden"
                            >
                                <Image
                                    src={tier.image}
                                    alt={`Beyond Rarity: ${tier.name}`}
                                    w="100%"
                                    h="100%"
                                    fit="cover"
                                />
                            </Box>
                        </AspectRatio>

                        <Flex direction="column">
                            <Box
                                fontFamily="regular"
                                fontSize={scale(windowWidth, minSize, maxSize, 36, 48)}
                                lineHeight="110%"
                            >
                                {tier.title}
                            </Box>
                            <Box
                                fontSize={scale(windowWidth, minSize, maxSize, 18, 24)}
                                fontWeight="light"
                            >
                                {tier.tagline}
                            </Box>
                        </Flex>

                        <Flex
                            direction="column"
                            w="100%"
                            px={4}
                            py={8}
                            bgColor={tier.pricBgColor}
                            borderRadius="15px"
                        >
                            <Box
                                fontFamily="regular"
                                fontSize={scale(windowWidth, minSize, maxSize, 24, 36)}
                                lineHeight="110%"
                            >
                                {tier.price}
                            </Box>
                            <Box
                                fontSize={scale(windowWidth, minSize, maxSize, 16, 24)}
                                fontWeight="light"
                                color="lavenderGray"
                            >
                                {tier.priceDescription}
                            </Box>
                            <Box w="100%" pt={4}>
                                {tier.priceAction}
                            </Box>
                        </Flex>

                        <Box
                            fontFamily="regular"
                            fontSize={scale(windowWidth, minSize, maxSize, 22, 24)}
                            fontWeight="bold"
                            lineHeight="110%"
                        >
                            {tier.featuresHeading}
                        </Box>

                        <Grid
                            templateColumns="auto 1fr"
                            templateRows={`repeat(1fr, ${tier.features.length})`}
                            columnGap={2}
                            rowGap={2}
                            textAlign="left"
                        >
                            {tier.features.map((point, index) => {
                                return (
                                    <Fragment key={`cp-check-${index}`}>
                                        <GridItem>
                                            <Flex h="100%" justify="center" align="start" pt="4px">
                                                <Icon
                                                    as={FiCheck}
                                                    fontSize={scale(
                                                        windowWidth,
                                                        minSize,
                                                        maxSize,
                                                        22,
                                                        28,
                                                    )}
                                                />
                                            </Flex>
                                        </GridItem>
                                        <GridItem>
                                            <Flex
                                                h="100%"
                                                justify="start"
                                                align="start"
                                                fontSize={scale(
                                                    windowWidth,
                                                    minSize,
                                                    maxSize,
                                                    18,
                                                    24,
                                                )}
                                                fontWeight="light"
                                            >
                                                {point}
                                            </Flex>
                                        </GridItem>
                                    </Fragment>
                                );
                            })}
                        </Grid>
                    </VStack>
                    {tier.isMostPopular && (
                        <Flex
                            pos="absolute"
                            direction="column"
                            top={-scale(windowWidth, minSize, maxSize, isDesktop ? 3 : 5, 5) + "px"}
                            right={
                                scale(windowWidth, minSize, maxSize, isDesktop ? 20 : 25, 30) + "px"
                            }
                            w={
                                scale(windowWidth, minSize, maxSize, isDesktop ? 50 : 65.6, 65.6) +
                                "px"
                            }
                            h="130px"
                            backgroundImage={`url('/icons/ribbon.svg')`}
                            backgroundRepeat="no-repeat"
                            backgroundPosition="top center"
                            bgSize="100% auto"
                            textAlign="center"
                            fontFamily="regular"
                            fontSize={scale(windowWidth, minSize, maxSize, isDesktop ? 10 : 14, 14)}
                            fontWeight="bold"
                            p="4px"
                        >
                            Most
                            <br />
                            Popular
                        </Flex>
                    )}
                </GridItem>
            </>
        );
    };

    return (
        <>
            <Grid
                // w="100%"
                maxW={isDesktop ? undefined : "400px"}
                templateColumns={isDesktop ? "1fr 1fr 1fr" : "auto"}
                templateRows={isDesktop ? "auto" : "1fr 1fr 1fr"}
                gap={8}
            >
                <TierGridItem tier={FREE_TIER} />

                <TierGridItem tier={PRO_TIER} />

                <TierGridItem tier={UBER_TIER} />
            </Grid>
        </>
    );
};

export default PricingTiersSection;
